import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import { Icon } from '../../components/ui/Icon';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { Chip, statusTone } from '../../components/ui/Chip';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { AnimatedNumber } from '../../components/ui/AnimatedNumber';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { fmtDate } from '../../lib/utils';
import { CandidateStage } from '@masamune/shared';

interface CandidateDetail {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  city: string | null;
  skill: string | null;
  seniority: string | null;
  availability: string | null;
  stage: CandidateStage;
  notes: string | null;
  enrollments: Array<{
    id: string; finalGrade: string | null; finalScore: number | null;
    course: { id: string; name: string; type: string; status: string };
    attendances: Array<{ status: string; lesson: { date: string; topic: string } }>;
  }>;
  placements: Array<{
    id: string; status: string; position: string | null; startDate: string | null;
    client: { id: string; legalName: string } | null;
  }>;
}

export function CandidateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: c, isLoading, isError } = useQuery({
    queryKey: ['candidate', id],
    queryFn: () => api.get<CandidateDetail>(`/candidates/${id}`),
    enabled: !!id,
    retry: false,
  });

  const move = useMutation({
    mutationFn: (stage: CandidateStage) => api.post(`/candidates/${id}/move`, { stage }),
    // Optimistic: lo stage cambia subito in UI, il server conferma (o si torna indietro)
    onMutate: async (stage) => {
      await qc.cancelQueries({ queryKey: ['candidate', id] });
      const prev = qc.getQueryData<CandidateDetail>(['candidate', id]);
      if (prev) qc.setQueryData<CandidateDetail>(['candidate', id], { ...prev, stage });
      return { prev };
    },
    onSuccess: (_d, stage) => {
      toast.ok('Stage aggiornato', stage.replace(/_/g, ' ').toLowerCase());
    },
    onError: (_e, _stage, ctx) => {
      if (ctx?.prev) qc.setQueryData(['candidate', id], ctx.prev);
      toast.alert('Spostamento fallito');
    },
    // In ogni caso riallinea col server (anche la lista candidati)
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['candidate', id] });
      qc.invalidateQueries({ queryKey: ['candidates'] });
    },
  });

  // Popover stage: Esc e click fuori chiudono (stesso pattern di Helper)
  const [stageOpen, setStageOpen] = useState(false);
  const stageRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!stageOpen) return;
    const onClick = (e: MouseEvent) => {
      if (stageRef.current && !stageRef.current.contains(e.target as Node)) setStageOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setStageOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [stageOpen]);

  const del = useMutation({
    mutationFn: () => api.delete(`/candidates/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['candidates'] });
      toast.ok('Candidato eliminato');
      navigate('/talent');
    },
    onError: (e: any) => toast.alert('Errore', e?.message ?? 'Riprova'),
  });

  if (!id) return <Navigate to="/talent" replace />;
  if (isError) return (
    <div className="page">
      <Breadcrumb items={[{ label: 'Talent', to: '/talent' }, { label: c ? `${c.firstName} ${c.lastName}` : 'Candidato' }]} />
      <div className="max-w-[480px] mx-auto py-20 text-center">
        <h1 className="t-h1">Candidato non trovato</h1>
        <div className="mute mt-2">L'ID richiesto non esiste.</div>
      </div>
    </div>
  );
  if (isLoading || !c) return <DetailSkeleton header="square" />;

  const stages = Object.values(CandidateStage);
  const totalAttendances = c.enrollments.reduce((a, e) => a + e.attendances.length, 0);
  const presentAttendances = c.enrollments.reduce((a, e) => a + e.attendances.filter((at) => at.status === 'PRESENTE' || at.status === 'RITARDO').length, 0);
  const attendancePct = totalAttendances > 0 ? Math.round((presentAttendances / totalAttendances) * 100) : null;
  const activePlacements = c.placements.filter((p) => p.status === 'SOMMINISTRATO' || p.status === 'ASSUNTO').length;

  const stageTone: 'ok' | 'warn' | 'alert' | 'info' =
    (['ASSUNTO', 'SOMMINISTRATO', 'PRONTO_PER_PLACEMENT', 'IDONEO'] as string[]).includes(c.stage) ? 'ok'
      : c.stage === CandidateStage.SCARTATO ? 'alert'
      : (['FREQUENTANTE', 'ISCRITTO_AL_CORSO', 'COLLOQUIO_FISSATO'] as string[]).includes(c.stage) ? 'warn'
      : 'info';

  return (
    <div className="page">
      <Breadcrumb items={[{ label: 'Talent', to: '/talent' }, { label: c ? `${c.firstName} ${c.lastName}` : 'Candidato' }]} />

      {/* Header editoriale: titolo leggero e grande, hairline aurora dal ::after */}
      <div className="page-ed-hd flex items-start justify-between gap-4">
        <div className="min-w-0 max-w-[760px]">
          {/* L1 protagonista: il nome (peso leggero, grande), tronca se lungo — niente avatar */}
          <h1 className="ed-title truncate">{c.firstName} {c.lastName}</h1>
          {/* L3 meta-indice: skill·seniority·città su UNA riga mute, valori --text */}
          <div className="detail-meta mt-2 truncate">
            {c.skill && <span className="v">{c.skill}</span>}
            {c.skill && c.seniority && <span className="mute-2">·</span>}
            {c.seniority && <span>{c.seniority}</span>}
            {(c.skill || c.seniority) && c.city && <span className="mute-2">·</span>}
            {c.city && <span>{c.city}</span>}
          </div>
          {/* L4 più arretrata: contatti, label mute, valori --text peso 400 */}
          <div className="detail-meta mt-1 truncate">
            {c.email && <span><span className="mute-2 mr-1.5">Email</span><span className="v">{c.email}</span></span>}
            {c.email && c.phone && <span className="mute-2">·</span>}
            {c.phone && <span><span className="mute-2 mr-1.5">Tel</span><span className="v">{c.phone}</span></span>}
          </div>
          {/* Stage + disponibilità: riga SOTTO, unico accento-colore = lo stage pipeline */}
          <div className="flex gap-1.5 mt-2.5 items-center">
            <Chip tone={stageTone}>{c.stage.replace(/_/g, ' ').toLowerCase()}</Chip>
            {c.availability && <span className="t-micro">{c.availability}</span>}
          </div>
        </div>
        <div className="flex gap-2 pb-1">
          <ActionsMenu
            variant="ghost"
            items={[
              {
                key: 'delete',
                label: 'Elimina candidato',
                icon: 'trash',
                danger: true,
                onClick: async () => {
                  if (await confirmDialog({ title: 'Elimina candidato', body: `Eliminare ${c.firstName} ${c.lastName}?`, danger: true, confirmLabel: 'Elimina' })) del.mutate();
                },
              },
            ]}
          />
        </div>
      </div>

      {/* Numeri NUDI sul foglio, separati da hairline: niente scatole né icone */}
      <div className="stat-band">
        <div>
          <div className="st-label">Presenze</div>
          <div className="st-value">
            {attendancePct != null
              ? <><AnimatedNumber value={attendancePct} /><span className="u">%</span></>
              : <span className="mute-2">—</span>}
          </div>
          {attendancePct == null && <div className="st-delta">no dati</div>}
        </div>
        <div>
          <div className="st-label">Corsi</div>
          <div className="st-value"><AnimatedNumber value={c.enrollments.length} /></div>
          <div className="st-delta">frequentati</div>
        </div>
        <div>
          <div className="st-label">Placement attivi</div>
          <div className="st-value"><AnimatedNumber value={activePlacements} /></div>
          <div className="st-delta">{c.placements.length} totali</div>
        </div>
      </div>

      <div className="grid gap-6" style={{ gridTemplateColumns: '2fr 1fr' }}>
        <div className="col gap-6 min-w-0">
          {/* Tabella full-bleed sul foglio: kicker micro + conteggio mono, niente card */}
          <div className="ed-section">
            <div className="data-hd">
              <span className="t-micro">Corsi frequentati</span>
              <span className="mono mute text-[12px]">{c.enrollments.length}</span>
            </div>
            <table className="tbl tbl--ed">
              <thead><tr><th>Corso</th><th>Tipo</th><th>Stato</th><th className="num">Lezioni</th><th>Voto finale</th></tr></thead>
              <tbody>
                {c.enrollments.map((e) => (
                  <tr key={e.id} onClick={() => navigate(`/courses/${e.course.id}`)}>
                    <td className="font-semibold">{e.course.name}</td>
                    <td><span className="chip-label">{e.course.type}</span></td>
                    <td><Chip tone={statusTone(e.course.status)}>{e.course.status.replace('_', ' ')}</Chip></td>
                    <td className="num mono">{e.attendances.length}</td>
                    <td>{e.finalGrade ? <span className="mono font-semibold">{e.finalGrade}</span> : <span className="mute-2">—</span>}</td>
                  </tr>
                ))}
                {!c.enrollments.length && <tr><td colSpan={5} className="text-center py-8 mute text-[13px]">Nessun corso</td></tr>}
              </tbody>
            </table>
          </div>

          {/* Tabella full-bleed sul foglio: kicker micro + conteggio mono, niente card */}
          <div className="ed-section">
            <div className="data-hd">
              <span className="t-micro">Placement</span>
              <span className="mono mute text-[12px]">{c.placements.length}</span>
            </div>
            <table className="tbl tbl--ed">
              <thead><tr><th>Azienda</th><th>Posizione</th><th>Inizio</th><th>Stato</th></tr></thead>
              <tbody>
                {c.placements.map((p) => (
                  <tr key={p.id} onClick={() => p.client && navigate(`/clients/${p.client.id}`)}>
                    <td className="font-semibold">{p.client?.legalName ?? '—'}</td>
                    <td>{p.position ?? '—'}</td>
                    <td className="mono mute">{fmtDate(p.startDate)}</td>
                    <td><Chip tone={statusTone(p.status)} dot>{p.status.replace(/_/g, ' ')}</Chip></td>
                  </tr>
                ))}
                {!c.placements.length && <tr><td colSpan={4} className="text-center py-8 mute text-[13px]">Nessun placement</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Colonna laterale: kicker-blocks senza bordi, separati da hairline */}
        <div className="min-w-0">
          <div>
            <div className="data-hd">
              <span className="t-micro">Stage</span>
            </div>
            <div>
              {/* Stage a un gesto: la pill è insieme indicatore e bottone — click apre la pipeline */}
              <div className="relative inline-block" ref={stageRef}>
                <button
                  type="button"
                  className={`chip chip-btn ${stageTone}`}
                  onClick={() => setStageOpen((v) => !v)}
                  disabled={move.isPending}
                  aria-haspopup="menu"
                  aria-expanded={stageOpen}
                  title="Sposta a…"
                >
                  <span className="font-semibold">{c.stage.replace(/_/g, ' ').toLowerCase()}</span>
                  <Icon name="chevronD" size={10} />
                </button>
                {stageOpen && (
                  <div
                    className="absolute left-0 mt-1.5"
                    style={{
                      background: 'var(--surface)',
                      border: '1px solid var(--line)',
                      borderRadius: 'var(--r-card)',
                      boxShadow: 'var(--elev-pop)',
                      padding: 8,
                      zIndex: 'var(--z-dropdown)',
                      maxWidth: 360,
                      width: 'max-content',
                    }}
                  >
                    <div className="flex flex-wrap" style={{ gap: 6 }}>
                      {stages.map((s) => (
                        <button
                          key={s}
                          type="button"
                          className={`chip chip-btn ${s === c.stage ? `${stageTone} opacity-60 cursor-default` : s === CandidateStage.SCARTATO ? 'alert' : ''}`}
                          disabled={s === c.stage || move.isPending}
                          onClick={() => { setStageOpen(false); move.mutate(s); }}
                        >
                          {s.replace(/_/g, ' ').toLowerCase()}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="ed-divider" />

          <div>
            <div className="data-hd">
              <span className="t-micro">Note</span>
            </div>
            {c.notes ? (
              <div className="text-[13px] whitespace-pre-wrap">{c.notes}</div>
            ) : (
              <div className="mute text-[13px] italic">Nessuna nota</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
